import { useState } from "react";

import firefliesLogo from "@/../../hypetrain/static/img/fireflies-logo-cc.png";
import { LazyImg } from "@/components/helpers/ui/LazyImg";
import {
  type InputField,
  IntegrationModal,
} from "@/components/ui/integration-modal";
import {
  useCreateFirefliesIntegrationMutation,
  useFirefliesIntegrationsQuery,
} from "@/graphql";

interface FirefliesIntegrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function FirefliesIntegrationModal({
  open,
  onOpenChange,
  onSuccess,
}: FirefliesIntegrationModalProps) {
  // Form state
  const [integrationName, setIntegrationName] = useState("");
  const [token, setToken] = useState("");
  const [connectionErrors, setConnectionErrors] = useState<string[]>([]);

  // GraphQL
  const { refetch: refetchIntegrations } = useFirefliesIntegrationsQuery();
  const [createIntegration, { loading }] =
    useCreateFirefliesIntegrationMutation();

  // Reset form
  const resetForm = () => {
    setIntegrationName("");
    setToken("");
    setConnectionErrors([]);
  };

  // Handle connection
  const handleConnect = async (values: Record<string, string>) => {
    setConnectionErrors([]);

    try {
      const result = await createIntegration({
        variables: {
          name: values["integration-name"],
          apiKey: values["api-token"],
        },
      });

      if (result.data?.createFirefliesIntegration?.ok) {
        resetForm();
        onOpenChange(false);
        await refetchIntegrations();
        onSuccess?.();
        return true;
      } else {
        const errors = result.data?.createFirefliesIntegration?.errors || [];
        const errorMessages = errors
          .map((error) => error?.message)
          .filter((message): message is string => Boolean(message));
        setConnectionErrors(errorMessages);
        return false;
      }
    } catch (error) {
      console.error("Failed to create Fireflies integration:", error);
      setConnectionErrors([
        "Failed to connect to Fireflies. Please check your credentials.",
      ]);
      return false;
    }
  };

  // Handle cancel
  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  // Input fields
  const inputs: InputField[] = [
    {
      id: "integration-name",
      label: "Integration Name",
      type: "text",
      placeholder: "e.g. Sales, Marketing, etc.",
      required: true,
      value: integrationName,
      onChange: setIntegrationName,
    },
    {
      id: "api-token",
      label: "API Token",
      type: "password",
      placeholder: "Enter your Fireflies API token",
      required: true,
      value: token,
      onChange: setToken,
    },
  ];

  return (
    <IntegrationModal
      open={open}
      onOpenChange={onOpenChange}
      integrationName="Fireflies"
      icon={
        <LazyImg
          src={firefliesLogo}
          alt="Fireflies"
          className="h-10 object-contain"
        />
      }
      description={
        <div className="flex flex-col gap-3">
          <p className="text-base font-normal leading-snug text-gray-800 m-0">
            Get an access token from your Fireflies workspace
          </p>
          <div className="text-base font-normal leading-relaxed">
            <ul className="list-disc ml-5 space-y-1">
              <li>
                Log in to your account at{" "}
                <a
                  href="https://app.fireflies.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 underline hover:text-blue-600"
                >
                  app.fireflies.ai
                </a>
              </li>
              <li>
                Navigate to the{" "}
                <a
                  href="https://app.fireflies.ai/settings"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 underline hover:text-blue-600"
                >
                  Settings
                </a>{" "}
                section
              </li>
              <li>Click on "Developer Settings"</li>
              <li>Copy your API key and input below</li>
            </ul>
          </div>
        </div>
      }
      inputs={inputs}
      onConnect={handleConnect}
      onCancel={handleCancel}
      loading={loading}
      errors={connectionErrors}
    />
  );
}
